import { AccountLinkModel, InstallationInvitationTableFormModel } from 'src/Libs/installation-rights-management/models'
import { useEffect, useState } from 'react'
import { useUserAccessAndInvitations } from 'src/Libs/installation-rights-management/hooks/index'

interface UseInstallationAccessFormHook {
  /**
   * Initiates the update of many AccountLink models at once.
   *
   * @param values
   */
  submitBulkUpdate: (values: InstallationInvitationTableFormModel) => Promise<void>
  /**
   * Provides a pre-configured form model representing a list of updatable AccountLink data.
   */
  formData: InstallationInvitationTableFormModel
}

const INITIAL_FORM = { items: [] } as InstallationInvitationTableFormModel

/**
 * Providing support to handle the update of many AccountLink entries at once.
 *
 * The hook provides the initial form data, to be used in a Formik context.
 *
 * And also a callback function to manage a bulk update for many AccountLinks.
 *
 * It is not supported by any of our APIs to bulk update AccountLink data, so this has to be initialized and managed
 * by the client integration for now..
 *
 * @param accountLinks
 */
const useInstallationAccessForm = (accountLinks: AccountLinkModel[]): UseInstallationAccessFormHook => {
  const { toggleWriteAccess } = useUserAccessAndInvitations()
  const [formData, setFormData] = useState<InstallationInvitationTableFormModel>(INITIAL_FORM)

  useEffect(() => {
    const formData = {
      items: [...accountLinks],
    } as InstallationInvitationTableFormModel
    setFormData(formData)
  }, [accountLinks])

  const submitBulkUpdate = async (values: InstallationInvitationTableFormModel): Promise<void> => {
    await Promise.all(
      values.items.map((formItem, index) => {
        const hasWriteAccessChanged = formData.items[index].hasWriteAccess !== formItem.hasWriteAccess
        const hasApiAccessChanged = formData.items[index].hasHagerEnergyApiAccess !== formItem.hasHagerEnergyApiAccess
        if (!hasWriteAccessChanged && !hasApiAccessChanged) return Promise.resolve(undefined)
        return toggleWriteAccess(formItem, formItem.hasWriteAccess, formItem.hasHagerEnergyApiAccess)
      })
    )
  }

  return {
    submitBulkUpdate,
    formData,
  }
}

export default useInstallationAccessForm
